import Box from "@mui/material/Box";
import { Theme, SxProps } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import type { TooltipProps } from "@mui/material/Tooltip";
import Typography, { TypographyProps } from "@mui/material/Typography";
import merge from "lodash/merge";
import throttle from "lodash/throttle";
import {
  ReactElement,
  MouseEvent,
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useClipboard } from "../../hooks/useClipboard";

export type MiddleEllipsisProps = {
  children?: string;
  clickToCopy?: boolean;
  tooltip?: ReactElement | number | string;
  wrapperSx?: SxProps<Theme>;
  TooltipProps?: Omit<TooltipProps, "title" | "children">;
} & Omit<TypographyProps, "component">;

const getCssStyle = (element: HTMLElement, prop: string): string =>
  window.getComputedStyle(element, null).getPropertyValue(prop);

const getComputedFont = (el: HTMLElement): string => {
  const fontWeight = getCssStyle(el, "font-weight") || "normal";
  const fontSize = getCssStyle(el, "font-size") || "15px";
  const fontFamily = getCssStyle(el, "font-family") || "Inter";

  return `${fontWeight} ${fontSize} ${fontFamily}`;
};

type GetTextWidth = {
  (text: string, font: string): number;
  canvas?: HTMLCanvasElement;
};

const getCanvas = (): HTMLCanvasElement => {
  // Canvas is not supported in test environment
  if (process.env.NODE_ENV === "test") {
    return {
      getContext: () => ({
        measureText: () => ({
          // `element.offsetWidth` is also always 0 (unless specified) so we return 0 to avoid truncating in tests
          width: 0,
        }),
      }),
    } as unknown as HTMLCanvasElement;
  }

  return document.createElement("canvas");
};

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 15px Inter").
 * @internal
 */
export const getTextWidth: GetTextWidth = (text: string, font: string): number => {
  // cache canvas object for better performance
  getTextWidth.canvas = getTextWidth.canvas || getCanvas();
  const { canvas } = getTextWidth;
  const context = canvas.getContext("2d");
  if (!context) {
    return 0;
  }
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};

getTextWidth.canvas = undefined;

const calculate = (
  originalText: string,
  elementRef: MutableRefObject<HTMLElement | null>,
  containerWidth: number,
): string => {
  if (!originalText || !elementRef.current) {
    return "";
  }

  if (originalText.length <= 5) {
    // No point adding an ellipsis if we'll end up with the same (or more) characters
    return originalText;
  }

  const textWidth = Math.ceil(getTextWidth(originalText, getComputedFont(elementRef.current)));

  if (textWidth > containerWidth) {
    const txtChars = originalText.length;
    const avgLetterSize = textWidth / txtChars;
    const canFit = containerWidth / avgLetterSize;
    // We add the ellipses to final the string which creates
    // unexpected additional width resulting in the string
    // exceeding the bounds of the element container. Removing
    // an extra character from each side (+1) balances this out.
    const delEachSide = (txtChars - canFit) / 2 + 1;
    const endLeft = Math.floor(txtChars / 2 - delEachSide);
    const startRight = Math.ceil(txtChars / 2 + delEachSide);
    const leftText = originalText.substring(0, endLeft).trimEnd() || originalText[0];
    const rightText =
      originalText.substring(startRight).trimStart() || originalText[originalText.length - 1];

    return `${leftText}…${rightText}`;
  }
  return originalText;
};

class MiddleEllipsisUnsupportedChildError extends Error {
  constructor(child: unknown) {
    super(
      `${JSON.stringify(
        child,
      )} is not a string. MiddleEllipsis only supports children of type string.`,
    );
  }
}

export const MiddleEllipsis = ({
  clickToCopy,
  tooltip,
  TooltipProps,
  onClick,
  sx,
  wrapperSx,
  children: originalText,
  ...typographyProps
}: MiddleEllipsisProps): ReactElement => {
  if (originalText && typeof originalText !== "string") {
    throw new MiddleEllipsisUnsupportedChildError(originalText);
  }

  const { copyToClipboard } = useClipboard();
  const elementRef = useRef<HTMLElement | null>(null);
  const valueRef = useRef<string>(originalText || "");
  const [length, setLength] = useState<number>(0);
  const [displayValue, setDisplayValue] = useState<string>(originalText || "");
  const hasEllipsis = displayValue !== originalText;

  const typographyStyle: SxProps<Theme> = useMemo(
    () =>
      merge(
        {
          ...(hasEllipsis ? { whiteSpace: "nowrap" } : {}),
        },
        sx,
        clickToCopy
          ? {
              cursor: "pointer",
              userSelect: "none",
              ":hover": {
                backgroundColor: "grey.A100",
                borderRadius: 0.5,
                boxShadow: (theme: Theme) => `0 0 0 2px ${theme.vars.palette.grey.A100}`,
              },
            }
          : {},
      ),
    [clickToCopy, hasEllipsis, sx],
  );
  const ref = useRef(undefined);

  const resizeObserver = useRef(
    new ResizeObserver((entries) => {
      if (ref.current) {
        setLength(entries[0].contentRect.width);
      }
    }),
  );

  useEffect(() => {
    valueRef.current = originalText || "";
    setDisplayValue(calculate(originalText || "", elementRef, length));
  }, [originalText, length]);

  // We add a throttle here to fix an issue where the rendering of a component
  // would overwrite the ellipsis on the first pixel when the screen size is
  // changed to use a new breakpoint. This would cause the ellipsis to not take
  // effect in these cases.
  useEffect(() => {
    const listener = throttle(
      () => {
        setDisplayValue(calculate(valueRef.current, elementRef, length));
      },
      50,
      { leading: false, trailing: true },
    );

    window.addEventListener("resize", listener);

    return () => {
      window.removeEventListener("resize", listener);
      listener.cancel();
    };
  }, [length]);

  useLayoutEffect(() => {
    const observer = resizeObserver.current;
    const element = ref.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [resizeObserver]);

  const setContainerRef = useCallback((node: HTMLDivElement) => {
    if (node) elementRef.current = node;
  }, []);

  const onTypographyClick = async (e: MouseEvent<HTMLDivElement>) => {
    onClick?.(e);
    if (clickToCopy) {
      await copyToClipboard(originalText as string);
    }
  };

  if (!originalText) {
    return <></>;
  }

  return (
    <Box sx={{ overflowX: "hidden", ...wrapperSx }} ref={ref}>
      <Tooltip
        enterDelay={1000}
        {...TooltipProps}
        title={tooltip || originalText || ""}
        disableHoverListener={!hasEllipsis}
        disableFocusListener={!hasEllipsis}
        disableTouchListener={!hasEllipsis}
      >
        <Typography
          {...typographyProps}
          component="span"
          ref={setContainerRef}
          onClick={onTypographyClick}
          sx={typographyStyle}
        >
          {displayValue}
        </Typography>
      </Tooltip>
    </Box>
  );
};
